import type {
  IChat,
  ICrop,
  IDocumentReference,
  IInputWithholdPeriod,
  IItem,
  ILink,
  INozzle,
  IOperator,
  IUnit,
  IWeatherStat,
} from './IInput';
import type { IInputStep } from './IInputStep';

export enum InputTaskType {
  Generic = 0,
  Application = 1,
  Production = 2,
  CropEstablishment = 3,
}

export const INPUT_TASK_TYPE_LABELS: Record<InputTaskType, string> = {
  [InputTaskType.Generic]: 'Generic',
  [InputTaskType.Application]: 'Application',
  [InputTaskType.Production]: 'Production',
  [InputTaskType.CropEstablishment]: 'Crop Establishment',
};

export const getInputTaskTypeLabel = (
  taskType?: InputTaskType | number | null
): string => {
  if (taskType === null || taskType === undefined) {
    return '-';
  }

  return (
    INPUT_TASK_TYPE_LABELS[taskType as InputTaskType] ??
    INPUT_TASK_TYPE_LABELS[InputTaskType.Generic]
  );
};

// Plan vs Actual split at task level.
export enum InputTaskCategory {
  Plan = 1,
  Actual = 2,
}

// Status options vary by category.
// Category=Plan allows Plan (default) and Not implemented.
// Category=Actual allows In progress (default) and Done.
// Otherwise, all statuses are available.
export enum InputTaskStatus {
  Unspecified = 0,
  Plan = 1,
  InProgress = 2,
  NotImplemented = 3,
  Done = 4,
}

export const INPUT_TASK_STATUS_LABELS: Record<InputTaskStatus, string> = {
  [InputTaskStatus.Unspecified]: 'Unspecified',
  [InputTaskStatus.Plan]: 'Plan',
  [InputTaskStatus.InProgress]: 'In progress',
  [InputTaskStatus.NotImplemented]: 'Not implemented',
  [InputTaskStatus.Done]: 'Done',
};

export const getAvailableInputTaskStatuses = (
  category?: InputTaskCategory | number | null
): InputTaskStatus[] => {
  if (category === InputTaskCategory.Plan) {
    return [InputTaskStatus.Plan, InputTaskStatus.NotImplemented];
  }

  if (category === InputTaskCategory.Actual) {
    return [InputTaskStatus.InProgress, InputTaskStatus.Done];
  }

  return [
    InputTaskStatus.Unspecified,
    InputTaskStatus.Plan,
    InputTaskStatus.InProgress,
    InputTaskStatus.NotImplemented,
    InputTaskStatus.Done,
  ];
};

export const getDefaultInputTaskStatus = (
  category?: InputTaskCategory | number | null
): InputTaskStatus => {
  if (category === InputTaskCategory.Plan) {
    return InputTaskStatus.Plan;
  }

  if (category === InputTaskCategory.Actual) {
    return InputTaskStatus.InProgress;
  }

  return InputTaskStatus.Unspecified;
};

export const getInputTaskStatusLabel = (
  status?: InputTaskStatus | number | null
): string => {
  if (status === null || status === undefined) {
    return '-';
  }

  return (
    INPUT_TASK_STATUS_LABELS[status as InputTaskStatus] ??
    INPUT_TASK_STATUS_LABELS[InputTaskStatus.Unspecified]
  );
};

export interface IInputTaskBase {
  dateCreated: string;
  creatorId: string | null;
  snapCreator: string | null;
  dateModified: string;
  modifierId: string | null;
  snapModifier: string | null;
  startDate: string | null;
  endDate: string | null;
  area: number | null;
  areaUnit: IUnit | null;
  timing: IItem | null;
  notes: string | null;
  taskId: string;
  category: InputTaskCategory;
  status: InputTaskStatus;
  startDateHasDay: boolean;
  endDateHasDay: boolean;
  chats: IChat[];
  operator: IOperator | null;
  taskType: InputTaskType;
  taskCopyFrom: IDocumentReference[];
  taskCopyTo: IDocumentReference[];
  priority: number;
  links: ILink[];
  steps: IInputStep[];
}

export interface IInputTaskGeneric extends IInputTaskBase {
  taskType: InputTaskType.Generic;
}

export interface IInputTaskApplication extends IInputTaskBase {
  taskType: InputTaskType.Application;
  waterRate: number | null;
  targetSpecies: IItem[];
  plantCondition: IItem | null;
  applicationMethod: IItem | null;
  weather: IItem | null;
  weatherStats: IWeatherStat[];
  soilMoisture: IItem | null;
  waterSource: IItem | null;
  nozzles: INozzle[];
  sprayCapacity: number | null;
  sprayCapacityUnit: IUnit | null;
  frost: number | null;
  frostUnit: IUnit | null;
  isBuffZone: boolean | null;
  bandPercent: number | null;
  susceptibleCrops: ICrop[];
  plantBack: number | null;
  plantBackUnit: IUnit | null;
  withholdPeriods: IInputWithholdPeriod[];
  rainfast: number | null;
  rainfastUnit: IUnit | null;
  coverPercent: number | null;
  drift: number | null;
  driftUnit: IUnit | null;
  reentry: number | null;
  reentryUnit: IUnit | null;
}

export interface IInputTaskProduction extends IInputTaskBase {
  taskType: InputTaskType.Production;
}

export interface IInputTaskCropEstablishment
  extends Omit<IInputTaskApplication, 'taskType' | 'nozzles'> {
  taskType: InputTaskType.CropEstablishment;
  nozzle: INozzle | null;
}

export type IInputTask =
  | IInputTaskGeneric
  | IInputTaskApplication
  | IInputTaskProduction
  | IInputTaskCropEstablishment;
